import { Component, EventListener } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';

import { IAnimIconApi } from './initLottie';

// add your states here, e.g. Hidden: 'state-a-animated-svg--hidden',
enum States {}

// add your selector here, e.g. Element = '.js-a-animated-svg',
enum Selectors {}

enum AnimateOn {
	none = 'none',
	auto = 'auto',
	hover = 'hover',
	click = 'click',
}

enum MobileAnimateOn {
	none = 'none',
	byScrollPosition = 'byScrollPosition',
}

interface IOptions {
	round?: boolean;
	offsetTop?: number;
	offsetBottom?: number;
}

function getCoveredDistanceInViewport($element: JQuery, options: IOptions = {}) {
	const { round = true, offsetTop = 0, offsetBottom = 0 } = options;
	const itemTop = parseFloat(((($element.offset() || {}) as any).top || 0).toString()) - offsetTop;
	const top = parseFloat(($(document).scrollTop() || 0).toString());
	const itemHeight = parseFloat(($element.height() || 0).toString());
	const viewportHeight = parseFloat(($(window).height() || 0).toString()) - offsetBottom - offsetTop;

	const availableSpace = viewportHeight - itemHeight;
	const coveredDistance = itemTop - top;
	const percent = ((coveredDistance * 100) / availableSpace - 100) * -1;

	return round ? Math.round(percent) : percent;
}

function isInViewport($element: JQuery, options: IOptions = {}) {
	const { offsetTop = 0, offsetBottom = 0 } = options;
	const itemTop = parseFloat(((($element.offset() || {}) as any).top || 0).toString());
	const top = parseFloat(($(document).scrollTop() || 0).toString());
	const itemHeight = parseFloat(($element.height() || 0).toString());
	const viewportHeight = parseFloat(($(window).height() || 0).toString());
	const upwardsDistance = (itemTop - viewportHeight - top - offsetTop) * -1;
	const downwardsDistance = itemTop + itemHeight - top + offsetBottom;
	return upwardsDistance >= 0 && downwardsDistance >= 0;
}

// The @Component decorator will connect the class with `data-g-name="AnimatedSvg"` elements.
@Component('AnimatedSvg')
export class AnimatedSvg extends GondelJqueryComponent {
	private pluginInstance?: IAnimIconApi;
	private animateOn: AnimateOn = AnimateOn.none;
	private mobileAnimateOn: MobileAnimateOn = MobileAnimateOn.none;
	private loop: boolean = false;
	private authorMode: boolean = false;
	private playCounter: number = 0;
	private isPlaying: boolean = false;
	private viewport: string = getCurrentViewport();
	private jsonPath: string;
	private lottieInitiated: boolean = false;

	public async sync() {
		const animateOn = this.$ctx.data('animate-on');
		const mobileAnimateOn = this.$ctx.data('mobile-animate-on');
		this.jsonPath = this.$ctx.data('json-path');
		this.loop = (this.$ctx.data('loop') || 'false').toString() === 'true' ? true : false;
		this.authorMode = this.$ctx.data('author-mode');
		if (animateOn) {
			this.animateOn = animateOn;
		}
		if (mobileAnimateOn) {
			this.mobileAnimateOn = mobileAnimateOn;
		}
		this.initializeAnimation();

		// FIX: @EventListener('mouseover') don't work in IE11
		this.$ctx.on('mouseenter', this.handleMouseOver);
		// FIX: @EventListener('click') don't work in IE11
		this.$ctx.on('click', this.handleClick);
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent({ viewport }: { viewport: string }) {
		this.viewport = viewport;
	}

	// don't work in IE11
	// @EventListener('click')
	public handleClick = () => {
		if (!this.pluginInstance) {
			return;
		}

		if (this.animateOn !== AnimateOn.click) {
			return;
		}
		const action = this.pluginInstance.isPaused ? 'play' : 'pause';
		this[action]();
	};

	// don't work in IE11
	// @EventListener('mouseover')
	public handleMouseOver = () => {
		if (this.animateOn !== AnimateOn.hover) {
			return;
		}
		this.play();
	};

	public stop() {
		this.unregisterLoadAnimationByScrollPosition();
		this.unregisterAnimatebyScrollPosition();
	}

	private get isMqSmallDown() {
		return ['xxsmall', 'xsmall', 'small'].indexOf(this.viewport) !== -1;
	}

	public play = () => {
		if (!this.pluginInstance) {
			return;
		}

		// don't allow to play when animation frame is defined by scroll position
		if (this.mobileAnimateOn === MobileAnimateOn.byScrollPosition && this.isMqSmallDown) {
			return;
		}

		this.playCounter += 1;

		if (!this.isPlaying && this.playCounter > 1) {
			this.isPlaying = true;
			this.pluginInstance.goToAndPlay(0);
			return;
		}

		this.isPlaying = true;
		this.pluginInstance.play();
	};

	public pause = () => {
		if (!this.pluginInstance) {
			return;
		}
		this.pluginInstance.pause();
	};

	public registerAnimatebyScrollPosition = () => {
		if (this.mobileAnimateOn !== MobileAnimateOn.byScrollPosition) {
			return;
		}
		if (this.isMqSmallDown) {
			this.handleAnimatebyScrollPosition();
		}
		$(document).on(`scroll.${this.id}animatedSvg`, () => {
			if (!this.isMqSmallDown) {
				return;
			}
			this.handleAnimatebyScrollPosition();
		});
	};

	public unregisterAnimatebyScrollPosition = () => {
		$(document).off(`scroll.${this.id}animatedSvg`);
	};

	private get id() {
		return this.jsonPath.replace(/\/| |\./g, '-').toLowerCase();
	}

	public registerLoadAnimationByScrollPosition = () => {
		if (this.authorMode) {
			this.prepareLottie();
		} else {
			this.handleLoadByScrollPosition();
			$(document).on(`scroll.${this.id}loadAnimationByScrollPosition`, this.handleLoadByScrollPosition);
		}
	};

	public unregisterLoadAnimationByScrollPosition = () => {
		$(document).off(`scroll.${this.id}loadAnimationByScrollPosition`);
	};

	private handleLoadByScrollPosition = async () => {
		if (
			!isInViewport(this.$ctx, {
				offsetTop: 200,
				offsetBottom: 200,
			}) ||
			this.lottieInitiated
		) {
			return;
		}
		this.prepareLottie();
	};

	private handleAnimatebyScrollPosition = () => {
		const coveredDistance = getCoveredDistanceInViewport(this.$ctx, {
			offsetTop: 50,
			offsetBottom: 50,
		});
		if (coveredDistance < 0 || coveredDistance > 100 || !this.pluginInstance) {
			return;
		}

		const frames = this.pluginInstance.getDuration(true);
		this.pluginInstance.goToAndStop(Math.round((frames * coveredDistance) / 100), true);
	};

	private initializeAnimation = async () => {
		if (this.jsonPath === '') {
			return;
		}

		this.registerLoadAnimationByScrollPosition();
		this.registerAnimatebyScrollPosition();
		if (this.animateOn === AnimateOn.auto) {
			this.play();
		}
	};

	private prepareLottie = async () => {
		this.lottieInitiated = true;
		this.unregisterLoadAnimationByScrollPosition();

		// tslint:disable-next-line
		const asyncModule = await import(/* webpackChunkName: "dynamic-imports/lottie" */ './initLottie');
		this.pluginInstance = asyncModule.initialize(this._ctx, this.jsonPath, {
			loop: this.loop,
			onComplete: () => {
				this.isPlaying = false;
			},
		});
	};
}
